import { useMutation } from '@apollo/client'
import { AddCircle, RemoveCircle } from '@mui/icons-material'
import {
    Box,
    Button,
    Divider,
    Rating,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { GetRosterCandidateClientNotesQuery } from '__generated__/graphql'
import { CreateRosterCandidateClientNote } from 'client/graphql/candidate-drawer.gql'
import { useState } from 'react'
import { Accordion } from 'shared/components/Accordion'
import ClientNote, { clientNotesDivName } from 'shared/components/clientNote'
import useToast from 'shared/components/Toast/useToast'
import { GetRosterCandidateClientNotes } from 'shared/graphql/clientNotes'
import { usePagination } from 'shared/hooks/usePagination'
import { getPxAsRem } from 'shared/utils'

interface ClientNoteProps {
    rosterCandidateId: string | undefined
    allowDeleteOverride?: boolean
    allowNewNote?: boolean
    disabled?: boolean
}

const ClientNotes: React.FC<ClientNoteProps> = ({
    rosterCandidateId,
    allowDeleteOverride = false,
    allowNewNote = true,
    disabled = false,
}) => {
    const [showFeedbackInput, setShowFeedbackInput] = useState(false)
    const [feedback, setFeedback] = useState('')
    const [ratingInput, setRatingInput] = useState(0)

    const { data, refetch } = usePagination<
        GetRosterCandidateClientNotesQuery['getRosterCandidateClientNotes']['data'][number]
    >({
        query: GetRosterCandidateClientNotes,
        skip: !rosterCandidateId,
        variables: { rosterCandidateId },
        queryPath: 'getRosterCandidateClientNotes',
        trackedDivClassName: clientNotesDivName,
        limit: 10,
    })

    const [addNote] = useMutation(CreateRosterCandidateClientNote)
    const toast = useToast()

    const handleRatingChange = (newValue: number | null) => {
        if (newValue !== null) {
            setRatingInput(newValue)
        }
    }

    const resetData = () => {
        setShowFeedbackInput(false)
        setFeedback('')
        setRatingInput(0)
    }

    return (
        <Accordion label="Client Feedback" isExpanded>
            <Stack direction="column" spacing={2}>
                <Stack direction="column" spacing={2}>
                    {allowNewNote && (
                        <Box display="flex" alignSelf={'flex-end'}>
                            <Button
                                variant="text"
                                disabled={disabled}
                                onClick={() => {
                                    setShowFeedbackInput(!showFeedbackInput)
                                }}
                            >
                                {showFeedbackInput ? (
                                    <RemoveCircle
                                        sx={{
                                            paddingRight: getPxAsRem(3),
                                            width: getPxAsRem(20),
                                            height: getPxAsRem(20),
                                        }}
                                    />
                                ) : (
                                    <AddCircle
                                        sx={{
                                            paddingRight: getPxAsRem(3),
                                            width: getPxAsRem(20),
                                            height: getPxAsRem(20),
                                        }}
                                    />
                                )}
                                {showFeedbackInput
                                    ? 'Close Feedback'
                                    : 'Add Feedback'}
                            </Button>
                        </Box>
                    )}
                    {allowNewNote && showFeedbackInput && (
                        <Stack direction="column" spacing={2}>
                            <TextField
                                variant="outlined"
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="Add feedback here..."
                                multiline
                                fullWidth
                                rows={6}
                                label="Note"
                                sx={{ minHeight: getPxAsRem(150) }}
                            ></TextField>
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                            >
                                <Stack direction="row" spacing={1}>
                                    <Typography variant="body1">
                                        {ratingInput.toFixed(1)}
                                    </Typography>
                                    <Rating
                                        precision={0.5}
                                        value={ratingInput}
                                        onChange={(_, newValue) => {
                                            handleRatingChange(newValue)
                                        }}
                                    />
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            resetData()
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            addNote({
                                                variables: {
                                                    rosterCandidateId:
                                                        rosterCandidateId ?? '',
                                                    note: feedback,
                                                    rating: ratingInput,
                                                },
                                                onCompleted: (res) => {
                                                    const { success, message } =
                                                        res.addRosterCandidateClientNote
                                                    toast({
                                                        title: success
                                                            ? 'Note Added'
                                                            : 'Error',
                                                        message:
                                                            message ??
                                                            (success
                                                                ? 'Client note added successfully'
                                                                : 'Unable to add client note, please try again'),
                                                        severity: success
                                                            ? 'success'
                                                            : 'error',
                                                    })
                                                    if (success) {
                                                        resetData()
                                                        refetch()
                                                    }
                                                },
                                                onError: (error) => {
                                                    console.error(
                                                        'Error adding note:',
                                                        error
                                                    )
                                                    toast({
                                                        title: 'Error',
                                                        message:
                                                            'Unable to add client note, please try again',
                                                        severity: 'error',
                                                    })
                                                },
                                            })
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                {allowNewNote && <Divider />}
                {data?.length > 0 ? (
                    data?.map((note) => (
                        <ClientNote
                            onDelete={refetch}
                            key={note.id}
                            noteId={note.id}
                            note={note.note ?? ''}
                            updatedAt={note.updatedAt}
                            mostRecentAuthor={{
                                id: note.createdByUser?.id ?? '',
                                preferredName:
                                    note.createdByUser?.preferredName ?? '',
                            }}
                            createdAt={note.createdAt}
                            rating={note.rating ?? 0}
                            allowDeleteOverride={allowDeleteOverride}
                        />
                    ))
                ) : (
                    <Typography variant="body1">No notes available</Typography>
                )}
            </Stack>
        </Accordion>
    )
}
export default ClientNotes
